import { useCallback, useEffect, useState } from 'react';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { api, getAccessToken } from '../api/apiClient';
import { useSelectedAccount } from '../App';
import { getPayNowLink } from '../utils/payNow';
import Modal from '../components/modal';
import DateRangeFilter from '../components/dateRangeFilters';

interface AccountEntryApi {
    summary: string;
    date: string;
    credit: number;
    debit: number;
    balance: number;
}

interface AccountEntryUi {
    id: number;
    summary: string;
    date: string;
    credit?: string;
    debit?: string;
    balance: string;
}

const currency = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'GBP' });

function formatDateShort(iso: string) {
    const d = new Date(iso);
    if (isNaN(d.getTime())) {
        return iso;
    }
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const MyAccount = () => {
    const [entries, setEntries] = useState<AccountEntryUi[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [tokenError, setTokenError] = useState<string | null>(null);
    const [modalType, setModalType] = useState<string | null>(null);
    const [fromDate, setFromDate] = useState<string>('1970-01-01');
    const [toDate, setToDate] = useState<string>('2100-01-01');
    const [resetTrigger, setResetTrigger] = useState(false);
    const [selectedSubAccount, setSelectedSubAccount] = useState<string>('MAIN');
    const { selectedAccount } = useSelectedAccount();
    const today = new Date();

    const rentChargeEffectives = selectedAccount?.rentChargeEffectives ?? [];

    const currentCharges = rentChargeEffectives.find((r: any) => {
        const from = new Date(r.effectiveFrom);
        const to = r.effectiveTo ? new Date(r.effectiveTo) : null;
        return today >= from && (!to || today <= to);
    });

    const futureCharges = rentChargeEffectives
        .filter((r: any) => new Date(r.effectiveFrom) > today)
        .sort((a: any, b: any) => new Date(a.effectiveFrom).getTime() - new Date(b.effectiveFrom).getTime())[0];

    const previousCharges = rentChargeEffectives
        .filter((r: any) => r.effectiveTo && new Date(r.effectiveTo) < today)
        .sort((a: any, b: any) => new Date(b.effectiveTo!).getTime() - new Date(a.effectiveTo!).getTime())[0];

    const loadToken = useCallback(async () => {
        try {
            setTokenError(null);
            const t = await getAccessToken();
            setToken(t);
            console.log(token);
            if (!t) {
                setTokenError('No access token available (silent acquisition returned null).');
            }
        } catch (e: any) {
            setToken(null);
            setTokenError(e?.message || 'Failed to acquire token');
            console.error(tokenError);
        }
    }, []);

    //const interactiveAcquire = useCallback(() => {
    //    const scopes = (loginRequest.scopes || []) as string[];
    //    instance.acquireTokenRedirect({
    //        scopes,
    //        account: instance.getActiveAccount() || accounts[0],
    //    }).catch(e => console.error('interactive acquireTokenRedirect failed', e));
    //}, [instance, accounts]);

    const load = useCallback(() => {
        if (!selectedAccount?.accountReference) {
            setEntries([]);
            return;
        }

        setLoading(true);
        setError(null);

        const controller = new AbortController();
        const signal = controller.signal;

        let isCurrent = true;

        let query = '';
        if (selectedSubAccount !== 'MAIN') {
            const sub = selectedAccount.subAccounts?.find(s => s.postingCode === selectedSubAccount);
            if (sub) {
                query = `/account/subaccount/statement?accountReference=${encodeURIComponent(selectedAccount.accountReference)}&postingCode=${encodeURIComponent(sub.postingCode)}&fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}`;
            }
        } else {
            query = `/account/statement?accountReference=${encodeURIComponent(selectedAccount.accountReference)}&fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}`;
        }

        api<AccountEntryApi[]>(query, { signal })
            .then(data => {
                if (!isCurrent) {
                    return;
                }

                let ui: AccountEntryUi[] = (Array.isArray(data) ? data : []).map((r, i) => ({
                    id: i,
                    summary: r.summary,
                    date: formatDateShort(r.date),
                    credit: r.credit ? currency.format(r.credit) : undefined,
                    debit: r.debit ? currency.format(r.debit) : undefined,
                    balance: currency.format(r.balance)
                }));

                ui = ui
                    .map((r, i) => ({ ...r, _originalIndex: i }))
                    .sort((a, b) => {
                        const dateDiff = new Date(b.date).getTime() - new Date(a.date).getTime();
                        if (dateDiff !== 0) {
                            return dateDiff;
                        }
                        return b._originalIndex - a._originalIndex;
                    })
                    .map(({ _originalIndex, ...rest }) => rest);

                setEntries(ui);
            })
            .catch(err => {
                if (!isCurrent) {
                    return;
                }
                if (err.name === 'AbortError') {
                    console.log('Previous request canceled');
                } else {
                    setError(err.message || 'Failed to load account entries');
                    console.error(error);
                }
            })
            .finally(() => {
                if (isCurrent) {
                    setLoading(false);
                }
            });

        return () => {
            isCurrent = false; // mark this request as outdated on cleanup
            controller.abort(); // abort the fetch
        };
    }, [selectedAccount, fromDate, toDate, selectedSubAccount]);

    useEffect(() => {
        const abortLoad = load();
        return () => abortLoad?.();
    }, [load, selectedAccount, selectedSubAccount]);


    useEffect(() => {
        loadToken();
    }, [loadToken]);

    const openModal = (accountInfoType: string) => {
        setModalType(accountInfoType);
    };

    const closeModal = () => {
        setModalType(null);
    };

    const renderChargesSection = (title: string, charges: any[]) => {
        if (!charges || charges.length === 0) {
            return null;
        }

        return (
            <div className="myaccount__charges-section">
                <h3 className="myaccount__charges-heading">{title}</h3>

                {charges.map((c: any, i: number) => (
                    <div key={i} className="myaccount__charges-row">
                        <span>{c.description}:</span>
                        <span>{currency.format(c.value)}</span>
                    </div>
                ))}
            </div>
        );
    };

    const handlePayNow = () => {
        const link = getPayNowLink(selectedAccount);
        if (link) {
            window.open(link, "_blank", "noopener,noreferrer");
        }
    };

    const handleContactUs = () => {
        const contactUrl = "https://www.accentgroup.org/contact-us/";
        window.open(contactUrl, "_blank", "noopener,noreferrer");
    };

    const handleFilterChange = (from: string, to: string) => {
        setFromDate(from || '1970-01-01');
        setToDate(to || '2100-01-01');
    };

    const handleSubAccountChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedSubAccount(e.target.value);
    };

    const handleFitlerResest = () => {
        setResetTrigger(prev => !prev);
        setFromDate('1970-01-01');
        setToDate('2100-01-01');
        setSelectedSubAccount('MAIN');
        load();
    };

    const columns: GridColDef[] = [
        {
            field: 'summary',
            headerName: 'Summary',
            minWidth: 250,
            flex: 1,
            renderCell: (params) => (
                <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {params.value}
                </div>
            ),
        },
        { field: 'date', headerName: 'Date', minWidth: 140, flex: 0.4 },
        { field: 'credit', headerName: 'Credit', minWidth: 120, flex: 0.4 },
        { field: 'debit', headerName: 'Debit', minWidth: 120, flex: 0.4 },
        { field: 'balance', headerName: 'Balance', minWidth: 140, flex: 0.4 },
    ];

    const balance = selectedAccount?.balance ?? 0;
    const balanceText = balance < 0 ? `${currency.format(Math.abs(balance))} (in credit)` : `${currency.format(balance)} (in debt)`;

    const subAccounts = [
        { name: 'Main', postingCode: 'MAIN' },
        ...(selectedAccount?.subAccounts || [])
    ];

    return (
        <div className="page myaccount">
            <div className="myaccount__top">
                <h2 className="myaccount__balance">{balanceText}</h2>
                <p className="myaccount__description">
                    Your account is currently {balanceText}.
                </p>

                <div className="myaccount__links">
                    {rentChargeEffectives.length > 0 && (
                        <button className="btn btn--text-link" onClick={() => openModal('breakdown')}>
                            View breakdown of charges
                        </button>
                    )}
                    {selectedAccount?.agreementDescription &&
                        <button className="btn btn--text-link" onClick={() => openModal('payment')}>
                            View payment agreement
                        </button>
                    }
                </div>

                <div className="myaccount__buttons">
                    <button className="btn btn--primary myaccount__button" onClick={handlePayNow}>
                        Pay now
                    </button>
                    <button className="btn btn--secondary myaccount__button" onClick={handleContactUs}>
                        Contact us
                    </button>
                </div>
                <div className="myaccount__filters">
                    <DateRangeFilter
                        onFilterChange={handleFilterChange}
                        cssClasses="date-range-filter--abs"
                        resetTrigger={resetTrigger}
                    />
                    <div className="input input--select">
                        <label className="input__label" htmlFor="subaccount-select">Account</label>
                        <select
                            id="subaccount-select"
                            className="input__inp myaccount__subaccount-select"
                            onChange={handleSubAccountChange}
                            value={selectedSubAccount}
                        >
                            {subAccounts.map((account) => (
                                <option key={account.postingCode} value={account.postingCode}>
                                    {account.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button className="btn btn--secondary" onClick={handleFitlerResest}>
                        Reset
                    </button>
                </div>
            </div>

            <div className="myaccount__grid">
                <DataGrid
                    rows={entries}
                    columns={columns}
                    disableColumnMenu
                    disableRowSelectionOnClick
                    loading={loading}
                    pageSizeOptions={[12]}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 12 } },
                    }}
                    sx={{
                        '& .MuiDataGrid-columnSeparator': { display: 'none' },
                        '& .MuiDataGrid-cell:focus': { outline: 'none' },
                        '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-cell': {
                            display: 'flex !important',
                            alignItems: 'center',
                            whiteSpace: 'normal !important',
                            overflow: 'visible !important',
                            lineHeight: 1.4,
                            paddingTop: '8px',
                            paddingBottom: '8px',
                        },

                        '& .MuiDataGrid-columnHeaderTitle': {
                            whiteSpace: 'normal',
                            lineHeight: 1.4,
                            overflow: 'visible',
                        },
                    }}
                    slotProps={{
                        loadingOverlay: {
                            variant: 'circular-progress',
                            noRowsVariant: 'circular-progress',
                        },
                    }}
                />
            </div>

            {modalType && (
                <Modal closeFn={closeModal} maxWidth="600px">
                    {modalType === 'breakdown' && (
                        <div className="myaccount__modal myaccount__modal--breakdown">
                            <h2 className="myaccount__modal-title">Your Charges Breakdown</h2>

                            {currentCharges && renderChargesSection('Current charges', currentCharges.rentCharges)}
                            {futureCharges && renderChargesSection(`Charges starting on ${formatDateShort(futureCharges.effectiveFrom)}`, futureCharges.rentCharges)}
                            {previousCharges && renderChargesSection(`Charges ended on ${formatDateShort(previousCharges.effectiveTo)}`, previousCharges.rentCharges)}

                            <button className="btn" onClick={closeModal}>
                                Close
                            </button>
                        </div>
                    )}

                    {modalType === 'payment' && (
                        <div className="myaccount__modal myaccount__modal--payment">
                            <h3 className="myaccount__modal-title">Your Live Agreement</h3>
                            <p className="myaccount__modal-text">
                                You have agreed to pay your charges plus 
                                <span> &pound;{selectedAccount?.agreementDescription}</span>
                            </p>
                            <button className="btn" onClick={closeModal}>
                                Close
                            </button>
                        </div>
                    )}
                </Modal>
            )}

            {/*{error && <p className="myaccount__error">Error: {error}</p>}*/}
            {/*{!error && !loading && entries.length === 0 && (*/}
            {/*    <p className="myaccount__no-entries">No account entries.</p>*/}
            {/*)}*/}
            {/*<details className="myaccount__token-section">*/}
            {/*    <summary className="myaccount__token-summary">Access Token (debug)</summary>*/}
            {/*    {token ? (*/}
            {/*        <pre className="myaccount__token-display">{token}</pre>*/}
            {/*    ) : (*/}
            {/*        <p className="myaccount__token-error">*/}
            {/*            {tokenError || 'No token available (user not signed in or scope not granted).'}*/}
            {/*        </p>*/}
            {/*    )}*/}
            {/*    <div className="myaccount__token-actions">*/}
            {/*        <button className="btn myaccount__button--small" onClick={loadToken}>Refresh Token</button>*/}
            {/*        {!token && (*/}
            {/*            <button className="btn myaccount__button--small" onClick={interactiveAcquire}>*/}
            {/*                Grant Consent / Re-auth*/}
            {/*            </button>*/}
            {/*        )}*/}
            {/*    </div>*/}
            {/*</details>*/}
        </div>
    );
};

export default MyAccount;
